import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const OrdersPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('MARKET'); // 'MARKET' or 'CLOSED'
  const [marketOrders, setMarketOrders] = useState([]);
  const [closedOrders, setClosedOrders] = useState([]);
  const [spinner, setSpinner] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('LoggedIn');
    if (!isLoggedIn || isLoggedIn !== '1') {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    pageRefresh();
  }, [navigate]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  const pageRefresh = useCallback(async () => {
    try {
      const uid = localStorage.getItem('userid');
      if (!uid) {
        toast.error('User ID not found');
        navigate('/login');
        return;
      }

      // Fetch both market and closed orders initially
      await fetchMarketOrders(uid);
      await fetchClosedOrders(uid);

      // Set up interval for live updates (every 3 seconds)
      const interval = setInterval(async () => {
        await fetchMarketOrders(uid);
        await fetchClosedOrders(uid);
      }, 3000);

      setRefreshInterval(interval);
    } catch (error) {
      console.error('Error in pageRefresh:', error);
      toast.error('Failed to load orders');
    }
  }, [navigate]);

  const fetchMarketOrders = async (uid) => {
    try {
      const url = `https://octafxx.in/Rest/orders_api/Market/${uid}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseJson = await response.json();
      setMarketOrders(responseJson);
      setSpinner(false);
    } catch (error) {
      console.error('Error fetching market orders:', error);
      if (marketOrders.length === 0) {
        toast.error('Failed to load market orders');
      }
    }
  };

  const fetchClosedOrders = async (uid) => {
    try {
      const url = `https://octafxx.in/Rest/orders_api/Exit/${uid}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseJson = await response.json();
      setClosedOrders(responseJson);
      setSpinner(false);
    } catch (error) {
      console.error('Error fetching closed orders:', error);
      if (closedOrders.length === 0) {
        toast.error('Failed to load closed orders');
      }
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return timeString;
    }
  };

  const formatDate = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      return date.toLocaleDateString();
    } catch (error) {
      return timeString;
    }
  };

  const formatDateTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      return date.toLocaleString();
    } catch (error) {
      return timeString;
    }
  };

  const getProfitLossColor = (profitLoss) => {
    const value = parseFloat(profitLoss) || 0;
    return value >= 0 ? 'text-success' : 'text-danger';
  };

  const getProfitLossText = (profitLoss) => {
    const value = parseFloat(profitLoss) || 0;
    return value >= 0 ? 'Profit' : 'Loss';
  };

  const MarketOrderCard = ({ item, index }) => {
    const profitLoss = parseFloat(item.profit_loss) || 0;
    const isBuy = item.trade_type === 'BUY';
    const isSell = item.trade_type === 'SELL';
    
    return (
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h6 className="card-title mb-1 fw-bold">{item.cpair || 'N/A'}</h6>
              <div className="d-flex flex-wrap">
                <span className="badge bg-light text-dark me-2 mb-1">
                  <i className="bi bi-clock me-1"></i>
                  Started: {formatTime(item.trade_start_time)}
                </span>
              </div>
            </div>
            
            <div className="col-md-3 text-center">
              <div className="mb-1">
                <span className="text-muted small">Trade Price</span>
              </div>
              <h5 className="fw-bold mb-2">${parseFloat(item.trade_price || 0).toFixed(2)}</h5>
              
              {isBuy && (
                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 p-2">
                  <i className="bi bi-arrow-up-circle me-1"></i>
                  BUY {item.lotsize || '0'} Lot
                </span>
              )}
              
              {isSell && (
                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 p-2">
                  <i className="bi bi-arrow-down-circle me-1"></i>
                  SELL {item.lotsize || '0'} Lot
                </span>
              )}
            </div>
            
            <div className="col-md-3 text-center">
              <div className="mb-1">
                <span className="text-muted small">Live P&L</span>
              </div>
              <h5 className={`fw-bold mb-2 ${getProfitLossColor(profitLoss)}`}>
                ${Math.abs(profitLoss).toFixed(2)}
              </h5>
              <span className={`badge ${profitLoss >= 0 ? 'bg-success' : 'bg-danger'} p-2`}>
                {getProfitLossText(profitLoss)}
              </span>
            </div>
            
            <div className="col-md-2 text-end">
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={() => toast.info('Close trade feature coming soon')}
              >
                <i className="bi bi-x-circle me-1"></i>
                Close Trade
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ClosedOrderCard = ({ item, index }) => {
    const profitLoss = parseFloat(item.profit_loss) || 0;
    const isBuy = item.trade_type === 'BUY';
    const isSell = item.trade_type === 'SELL';
    
    return (
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                {isBuy && (
                  <span className="badge bg-success me-2">BUY</span>
                )}
                {isSell && (
                  <span className="badge bg-danger me-2">SELL</span>
                )}
                <div>
                  <h6 className="card-title mb-0 fw-bold">{item.cpair || 'N/A'}</h6>
                  <small className="text-muted">{item.lotsize || '0'} Lot @ ${parseFloat(item.trade_price || 0).toFixed(2)}</small>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="mb-1">
                <span className="text-muted small">Duration</span>
              </div>
              <div className="small">
                <div>
                  <i className="bi bi-play-fill text-primary me-1"></i>
                  {formatDateTime(item.trade_start_time)}
                </div>
                <div>
                  <i className="bi bi-stop-fill text-danger me-1"></i>
                  {formatDateTime(item.trade_end_time)}
                </div>
              </div>
            </div>
            
            <div className="col-md-3 text-center">
              <div className="mb-1">
                <span className="text-muted small">Final P&L</span>
              </div>
              <h4 className={`fw-bold mb-2 ${getProfitLossColor(profitLoss)}`}>
                ${Math.abs(profitLoss).toFixed(2)}
              </h4>
              <span className={`badge ${profitLoss >= 0 ? 'bg-success' : 'bg-danger'} p-2`}>
                <i className={`bi ${profitLoss >= 0 ? 'bi-trophy' : 'bi-exclamation-triangle'} me-1`}></i>
                {getProfitLossText(profitLoss)}
              </span>
            </div>
            
            <div className="col-md-2 text-end">
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => toast.info('Trade details coming soon')}
              >
                <i className="bi bi-file-earmark-text me-1"></i>
                Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getActiveOrders = () => {
    return activeTab === 'MARKET' ? marketOrders : closedOrders;
  };

  const getOrdersStats = () => {
    const orders = getActiveOrders();
    const totalProfit = orders.reduce((sum, order) => {
      const profit = parseFloat(order.profit_loss) || 0;
      return profit > 0 ? sum + profit : sum;
    }, 0);
    
    const totalLoss = orders.reduce((sum, order) => {
      const profit = parseFloat(order.profit_loss) || 0;
      return profit < 0 ? sum + profit : sum;
    }, 0);
    
    const netPnl = orders.reduce((sum, order) => {
      return sum + (parseFloat(order.profit_loss) || 0);
    }, 0);
    
    const winRate = orders.length > 0 
      ? (orders.filter(order => parseFloat(order.profit_loss) > 0).length / orders.length * 100).toFixed(1)
      : 0;
    
    return { totalProfit, totalLoss, netPnl, winRate };
  };

  const stats = getOrdersStats();

  if (spinner && marketOrders.length === 0 && closedOrders.length === 0) {
    return (
      <div className="container-fluid bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-light min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
    
      <div className="container py-4">
        {/* Tabs Navigation */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body p-0">
            <ul className="nav nav-tabs" id="ordersTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'MARKET' ? 'active' : ''}`}
                  onClick={() => setActiveTab('MARKET')}
                >
                  <i className="bi bi-graph-up me-2"></i>
                  Market Orders
                  {marketOrders.length > 0 && (
                    <span className="badge bg-primary ms-2">{marketOrders.length}</span>
                  )}
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'CLOSED' ? 'active' : ''}`}
                  onClick={() => setActiveTab('CLOSED')}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Closed Orders
                  {closedOrders.length > 0 && (
                    <span className="badge bg-secondary ms-2">{closedOrders.length}</span>
                  )}
                </button>
              </li>
            </ul>
            
            <div className="tab-content p-4">
              {/* Stats Overview */}
          
              {/* Orders List */}
              {getActiveOrders().length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <i className={`bi ${activeTab === 'MARKET' ? 'bi-graph-up' : 'bi-check-circle'} display-1 text-muted`}></i>
                  </div>
                  <h4 className="text-muted mb-3">
                    No {activeTab === 'MARKET' ? 'Active' : 'Closed'} Orders
                  </h4>
                  <p className="text-muted mb-4">
                    {activeTab === 'MARKET' 
                      ? "You don't have any active market orders at the moment."
                      : "You don't have any closed orders in your history."
                    }
                  </p>
                  {activeTab === 'MARKET' && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigate('/new-trade')}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Place Your First Trade
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  {activeTab === 'MARKET' 
                    ? marketOrders.map((item, index) => (
                        <MarketOrderCard key={index} item={item} index={index} />
                      ))
                    : closedOrders.map((item, index) => (
                        <ClosedOrderCard key={index} item={item} index={index} />
                      ))
                  }
                </div>
              )}
            </div>
          </div>
        </div>

    
      </div>

      {/* Footer */}
      <footer className="bg-dark text-white py-3 mt-4">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <small className="opacity-75">
                Showing: {activeTab === 'MARKET' ? 'Live Market Orders' : 'Closed Orders History'}
              </small>
            </div>
            <div className="col-md-6 text-md-end">
              <small className="opacity-75">
                <i className="bi bi-clock me-1"></i>
                Updated: {new Date().toLocaleTimeString()} | 
                <span className="ms-2">
                  Total: {marketOrders.length} Active, {closedOrders.length} Closed
                </span>
              </small>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OrdersPage;